import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactElement, ReactNode } from 'react';
import { useRef } from 'react';
import { Header } from 'semantic-ui-react';
import type { StringKeyOf } from 'type-fest';

import type { Filter } from '@models';
import {
  activeFilterKeys,
  FilterBar,
  FilterPicker,
  withFilter,
} from '@components/filters';
import { useComputedStylePx } from '@hooks/use-computed-style-px';
import { useDynamicCSSVarPx } from '@hooks/use-dynamic-css-var-px';
import { useIsPinned } from '@hooks/use-is-pinned';
import { resetScrollToTop } from '@utils/scroll-reset';

import s from './list-actions-header.scss';

export interface ListActionsHeaderProps<State> {
  /**
   * Action buttons (Export, Print, etc.) rendered to the right of the Filter
   * button. Use `ToolbarButton` for consistent styling.
   */
  actions?: ReactNode;
  className?: string;
  /**
   * Reference state used by the FilterBar's Reset button.
   */
  defaultFilterValues?: Partial<State>;
  /**
   * Available filter definitions for the FilterPicker. Omit to hide the
   * picker entirely (e.g. a list with no filtering).
   */
  filterOptions?: Filter<StringKeyOf<State>>[];
  /**
   * Current filter values. Active filters render as pills on a second row
   * below the header.
   */
  filterValues?: Partial<State>;
  /**
   * Optional icon rendered to the left of the title.
   */
  icon?: ReactElement;
  /**
   * Heading level for the title element. Defaults to 2.
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * Called with the next filter values when the user picks a filter or edits
   * an active pill.
   */
  onFilterChange?: (next: Partial<State>) => void;
  /**
   * Optional title. Omit for a headerless toolbar (picker + actions only).
   */
  title?: ReactNode;
}

/**
 * A standardized header row for tables and other list views. Provides:
 *
 * - Row 1: optional title (with icon) on the left, actions + Filter button
 *   on the right.
 * - Row 2: active filter pills + Reset, rendered only when there's a filter
 *   applied or Reset is appropriate.
 *
 * Composes `FilterPicker` and `FilterBar` internally so the call site only
 * needs to supply filter options, values, and a change handler.
 *
 * This is NOT a replacement for `Page.Header` — it sits inside `Page.Content`
 * as a section header above a list/table.
 */
export const ListActionsHeader = <State,>({
  actions,
  className,
  defaultFilterValues,
  filterOptions,
  filterValues,
  icon,
  level = 2,
  onFilterChange,
  title,
}: ListActionsHeaderProps<State>) => {
  useStyles(s);

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const stickyTopPx = useComputedStylePx(rootRef, 'top');
  const isPinned = useIsPinned(scrollTargetRef, stickyTopPx);

  useDynamicCSSVarPx(rootRef, { height: '--list-actions-header-height' });

  // Scroll the user back to the top of the affected list when the header is
  // currently sticky-pinned.
  const handleFilterChange = (next: Partial<State>) => {
    if (!onFilterChange) return;
    onFilterChange(next);

    const element = rootRef.current;
    const scrollTarget = scrollTargetRef.current;
    if (!element || !scrollTarget) return;

    requestAnimationFrame(() => {
      const lahTop = element.getBoundingClientRect().top;
      const targetTop = scrollTarget.getBoundingClientRect().top;

      if (lahTop > targetTop + 0.5) {
        resetScrollToTop(scrollTarget);
      }
    });
  };

  // Adding a filter from the picker just creates an empty pill — no data
  // change, no refetch, so no scroll reset.
  const handleSelect = (filter: Filter<StringKeyOf<State>>) => {
    onFilterChange?.(withFilter(filterValues ?? {}, filter));
  };

  const selectedKeys = activeFilterKeys(filterValues ?? {});

  const headingAs = `h${level}` as const;

  return (
    <>
      <div ref={scrollTargetRef} aria-hidden className={s.scrollTarget} />
      <div
        ref={rootRef}
        className={classNames(s.root, isPinned && s.pinned, className)}
      >
        <div className={s.topRow}>
          {title && (
            <Header as={headingAs} className={s.title}>
              {icon}
              <Header.Content>{title}</Header.Content>
            </Header>
          )}

          <div className={s.actions}>
            {filterOptions && (
              <FilterPicker
                onSelect={handleSelect}
                options={filterOptions}
                selectedKeys={selectedKeys}
              />
            )}

            {actions}
          </div>
        </div>

        {filterOptions && filterValues && onFilterChange && (
          <FilterBar
            className={s.filterRow}
            defaultValues={defaultFilterValues}
            filters={filterOptions}
            onChange={handleFilterChange}
            values={filterValues}
          />
        )}
      </div>
    </>
  );
};

export default ListActionsHeader;
